import { PageController } from '@client/components/_core/layout/PageController';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { Link } from '@client/components/Common/Link';
import { ClaimCompletionSetting } from '@client/components/claims/completion/ClaimCompletionSetting';
import { LodgementLinksTable } from '@client/components/lodgement/links/LodgementLinksTable';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useClaimDeferredBindingEnabled } from '@client/hooks/business/claim/useClaimDeferredBinding';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { trpcOptions } from '@client/trpc/client';
import { formatShortVersion } from '@client/utils/formatters/version-label';
import { CLAIM_DEFERRED_BINDING_FLAG_KEY } from '@shared/constants';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { FileText, Megaphone } from 'lucide-react';

/**
 * The organisation's claim intake links (CUR-2182, design frame 1c): links that take a first
 * notice of a claim from anyone, without its product. Each submission becomes a claim awaiting its
 * product. Product links stay on their product; these belong to no product, so they live here.
 */
export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claim-intake-links')({
    staticData: {
        metadata: {
            title: 'Claim intake links',
        },
        ui: {
            icon: Megaphone,
            title: 'Claim intake links',
            subtitle:
                'Links that take a first notice of a claim without its product. Each claim waits for its product in the Awaiting product view of the claims list.',
            menuDescription: 'Public links that report a claim before its product is known',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:read'],
        // Deferred binding needs products v2 as well; the page checks both and the server enforces both.
        featureFlag: CLAIM_DEFERRED_BINDING_FLAG_KEY,
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimIntakeLinks />
        </PageController>
    );
}

function ClaimIntakeLinks() {
    const { can } = usePermissions();
    const enabled = useClaimDeferredBindingEnabled();
    if (!enabled) return null;
    return (
        <div className="flex flex-col gap-4">
            <IntakeFormCallout canEditForm={can('settings:update')} />
            <LodgementLinksTable scope={{ kind: 'NOTIFICATION' }} canWrite={can('settings:update')} />
            <ClaimCompletionSetting canWrite={can('settings:update')} />
        </div>
    );
}

/**
 * The form claim intake links serve, and where to extend it: the organisation's own, unless a link
 * names another claim intake form (CUR-2194), which the table's Form column then shows.
 */
function IntakeFormCallout({ canEditForm }: { canEditForm: boolean }) {
    const orgId = useOrgId();
    const setup = useQuery(
        trpcOptions.lodgementLink.getNotificationLinkSetup.queryOptions(undefined, {
            meta: { errorSurface: 'silent' },
        }),
    );
    // Nothing to say until the setup is known: a failed read must not claim that no form exists yet.
    // The callout only explains; the links table below reports its own errors.
    if (!setup.isSuccess) return null;
    const form = setup.data?.form ?? null;
    const servableForms = (setup.data?.intakeForms ?? []).filter((intake) => intake.activeVersion !== null).length;

    return (
        <Callout variant="info" icon={<FileText size={16} />}>
            <span className="text-foreground">
                {form ? (
                    <>
                        {servableForms > 1 ? 'Claim intake links serve the' : 'Every claim intake link serves the'}{' '}
                        {canEditForm ? (
                            <Link
                                to="/console/$orgId/configuration/claims-products/forms/$formDefinitionId"
                                params={{ orgId, formDefinitionId: form.id.toString() }}
                            >
                                {form.name}
                            </Link>
                        ) : (
                            form.name
                        )}
                        {form.activeVersion === null ? '' : ` ${formatShortVersion(form.activeVersion)}`}
                        {servableForms > 1
                            ? ' unless they name another claim intake form, as the Form column shows. Add questions in the form designer; the core questions stay, because the claim is built from them.'
                            : '. Add questions to it in the form designer; the core questions stay, because the claim is built from them.'}
                    </>
                ) : canEditForm ? (
                    'Your first claim intake link creates the Claim intake form: who is reporting, what happened, when and where. You can then add questions to it in the form designer.'
                ) : (
                    "The organisation's first claim intake link creates the Claim intake form: who is reporting, what happened, when and where. Only someone who can change organisation settings can create links or add questions to the form."
                )}
            </span>
        </Callout>
    );
}
